/**
 * Compact, proximity-aware date formatting for board cards and similar
 * dense surfaces. Returns short strings so cards stay narrow:
 *   "Today", "Tomorrow", "Yesterday", "Mon", "Apr 12", "Apr 12, 2025"
 */

export type DueProximity = 'overdue' | 'today' | 'tomorrow' | 'this-week' | 'later' | 'past';

export interface FormattedDue {
  label: string;
  proximity: DueProximity;
}

const startOfDay = (d: Date): Date => {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
};

const daysBetween = (a: Date, b: Date): number => {
  const ms = startOfDay(b).getTime() - startOfDay(a).getTime();
  return Math.round(ms / (24 * 60 * 60 * 1000));
};

export const formatDue = (iso: string | null | undefined, now: Date = new Date()): FormattedDue | null => {
  if (!iso) return null;
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return null;

  const delta = daysBetween(now, due);

  if (delta < 0) {
    return { label: shortDate(due, now), proximity: 'overdue' };
  }
  if (delta === 0) {
    return { label: 'Today', proximity: 'today' };
  }
  if (delta === 1) {
    return { label: 'Tomorrow', proximity: 'tomorrow' };
  }
  if (delta < 7) {
    return {
      label: due.toLocaleDateString(undefined, { weekday: 'short' }),
      proximity: 'this-week',
    };
  }
  return { label: shortDate(due, now), proximity: 'later' };
};

const shortDate = (d: Date, now: Date): string => {
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

// ──────────────────────────────────────────────────────────────────────
//  Calendar grid helpers
// ──────────────────────────────────────────────────────────────────────

export interface CalendarDay {
  /** Local-time Date snapped to midnight. */
  date: Date;
  /** ISO date-only string `YYYY-MM-DD` (local) — handy as a Map key. */
  isoDate: string;
  /** False for the leading/trailing days that fall outside the focal month. */
  isCurrentMonth: boolean;
  /** True when this cell represents today (in the user's local timezone). */
  isToday: boolean;
}

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const isToday = (d: Date, now: Date = new Date()): boolean => isSameDay(d, now);

const localIsoDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

export const formatMonthYear = (d: Date): string =>
  d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

/**
 * Generate the 42-cell month grid (6 rows × 7 cols) for the focal month.
 * Includes leading days from the previous month and trailing days from the
 * next month so the grid is always rectangular.
 *
 * `firstDayOfWeek` = 0 (Sunday) or 1 (Monday). v1 hardcodes Monday at
 * call sites — kept as a parameter so a per-user / per-org preference
 * can plug in later without touching the algorithm.
 */
export const monthGridDays = (
  focalMonth: Date,
  firstDayOfWeek: 0 | 1 = 1,
  now: Date = new Date(),
): CalendarDay[] => {
  const year = focalMonth.getFullYear();
  const month = focalMonth.getMonth();

  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);

  // How many days of the previous month should lead the grid?
  // Sunday-start: shift = firstOfMonth.getDay()
  // Monday-start: shift = (firstOfMonth.getDay() + 6) % 7
  const shift = (firstOfMonth.getDay() - firstDayOfWeek + 7) % 7;

  const days: CalendarDay[] = [];

  // Leading days from the previous month.
  for (let i = shift; i > 0; i--) {
    const date = new Date(year, month, 1 - i);
    days.push({
      date,
      isoDate: localIsoDate(date),
      isCurrentMonth: false,
      isToday: isToday(date, now),
    });
  }

  // Days in the focal month.
  for (let d = 1; d <= lastOfMonth.getDate(); d++) {
    const date = new Date(year, month, d);
    days.push({
      date,
      isoDate: localIsoDate(date),
      isCurrentMonth: true,
      isToday: isToday(date, now),
    });
  }

  // Trailing days from the next month — always pad to 42 cells.
  while (days.length < 42) {
    const last = days[days.length - 1];
    if (!last) break;
    const next = new Date(last.date);
    next.setDate(last.date.getDate() + 1);
    days.push({
      date: next,
      isoDate: localIsoDate(next),
      isCurrentMonth: next.getMonth() === month && next.getFullYear() === year,
      isToday: isToday(next, now),
    });
  }

  return days;
};

/**
 * Day-of-week headers honoring `firstDayOfWeek`. Returns 7 short labels
 * (Mon, Tue, ...) using the user's locale.
 */
export const weekdayHeaders = (firstDayOfWeek: 0 | 1 = 1): string[] => {
  // Pick a known reference week (any Sunday) and rotate.
  const ref = new Date(2024, 0, 7); // Sunday Jan 7, 2024
  const labels: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(ref);
    d.setDate(ref.getDate() + ((i + firstDayOfWeek) % 7));
    labels.push(d.toLocaleDateString(undefined, { weekday: 'short' }));
  }
  return labels;
};

/** Snap a local Date to its `YYYY-MM-DD` ISO date string. */
export const toIsoDate = localIsoDate;

/**
 * Generate the 7 days of the week containing `focalDate`, anchored to
 * `firstDayOfWeek`. Each day is a `CalendarDay` with `isCurrentMonth`
 * flagged true (week view doesn't have the leading/trailing concept;
 * the property is preserved so consumers can share cell components
 * with the month grid).
 */
export const weekGridDays = (
  focalDate: Date,
  firstDayOfWeek: 0 | 1 = 1,
  now: Date = new Date(),
): CalendarDay[] => {
  const shift = (focalDate.getDay() - firstDayOfWeek + 7) % 7;
  const start = new Date(
    focalDate.getFullYear(),
    focalDate.getMonth(),
    focalDate.getDate() - shift,
  );

  const days: CalendarDay[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    days.push({
      date,
      isoDate: localIsoDate(date),
      isCurrentMonth: true,
      isToday: isToday(date, now),
    });
  }
  return days;
};

/**
 * Format a week range for display:
 *  - same month: "Nov 24 – 30, 2026"
 *  - same year:  "Nov 30 – Dec 6, 2026"
 *  - cross-year: "Dec 30, 2025 – Jan 5, 2026"
 */
export const formatWeekRange = (focalDate: Date, firstDayOfWeek: 0 | 1 = 1): string => {
  const days = weekGridDays(focalDate, firstDayOfWeek);
  const first = days[0]?.date;
  const last = days[6]?.date;
  if (!first || !last) return '';

  const sameYear = first.getFullYear() === last.getFullYear();
  const sameMonth = sameYear && first.getMonth() === last.getMonth();

  if (sameMonth) {
    const month = first.toLocaleDateString(undefined, { month: 'short' });
    return `${month} ${first.getDate()} – ${last.getDate()}, ${first.getFullYear()}`;
  }
  if (sameYear) {
    const a = first.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const b = last.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return `${a} – ${b}, ${first.getFullYear()}`;
  }
  const a = first.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const b = last.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${a} – ${b}`;
};

/** Bucket a list of items by the local-ISO date their `due_at` falls on. */
export const bucketByDueDate = <T>(
  items: ReadonlyArray<T>,
  getDueAt: (item: T) => string | null | undefined,
): Map<string, T[]> => {
  const out = new Map<string, T[]>();
  for (const item of items) {
    const due = getDueAt(item);
    if (!due) continue;
    const d = new Date(due);
    if (Number.isNaN(d.getTime())) continue;
    const key = localIsoDate(d);
    const bucket = out.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      out.set(key, [item]);
    }
  }
  return out;
};
